/**
 * Product stories.
 *
 * A rail of 9:16 covers that opens into a full-screen player with segmented
 * progress bars, tap-left/right navigation, hold-to-pause and product chips.
 * Modelled on the Instagram grammar people already know, so the affordances are
 * deliberately unlabelled — the segments at the top are the only instruction.
 *
 * Progress is driven by the video's own `timeupdate` where a video exists, and
 * by a Motion tween where the story is a still. Using the media's real clock
 * rather than a parallel timer is what keeps the bar honest when a video stalls
 * on a slow connection.
 */
import { animate } from '@theme/motion';
import { prefersReducedMotion } from '@theme/utilities';

/** How long a still image holds before advancing. */
const STILL_DURATION = 5;

class StoryPlayer extends HTMLElement {
  #index = 0;
  #tween;
  #controller;
  #holdTimer;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.slides = Array.from(this.querySelectorAll('[data-story-slide]'));
    this.bars = Array.from(this.querySelectorAll('[data-story-bar]'));
    if (!this.dialog || this.slides.length === 0) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Delegated, not bound per trigger. The marquee clones the cards after this
    // runs, and cloneNode copies markup but not listeners - a per-trigger
    // binding left every clone inert, which is most of the cards on screen.
    this.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest?.('[data-story-open]');
        if (!trigger || !this.contains(trigger)) return;
        this.open(Number(trigger.dataset.storyOpen) || 0);
      },
      { signal }
    );

    this.querySelector('[data-story-close]')?.addEventListener('click', () => this.close(), { signal });
    this.querySelector('[data-story-prev]')?.addEventListener('click', () => this.go(-1), { signal });
    this.querySelector('[data-story-next]')?.addEventListener('click', () => this.go(1), { signal });

    // Hold anywhere on the media to pause — the same gesture the grammar
    // already trains, and it doubles as "let me read this chip".
    const stage = this.querySelector('[data-story-stage]');
    stage?.addEventListener('pointerdown', () => this.#hold(), { signal });
    for (const event of ['pointerup', 'pointercancel', 'pointerleave']) {
      stage?.addEventListener(event, () => this.#release(), { signal });
    }

    this.dialog.addEventListener('close', () => this.#stop(), { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    this.#stop();
    this.#controller?.abort();
  }

  #onKeydown = (event) => {
    if (event.key === 'ArrowRight') this.go(1);
    else if (event.key === 'ArrowLeft') this.go(-1);
  };

  open(index = 0) {
    this.#index = index;
    this.dialog.showModal();
    this.#render();
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  go(delta) {
    const next = this.#index + delta;
    if (next < 0) return;
    if (next >= this.slides.length) {
      this.close();
      return;
    }
    this.#index = next;
    this.#render();
  }

  #hold() {
    // A tap is not a hold. Only treat the press as a pause once it outlives the
    // gesture that would have been a tap-to-advance.
    this.#holdTimer = setTimeout(() => {
      this.#tween?.pause();
      this.#currentVideo?.pause();
    }, 180);
  }

  #release() {
    clearTimeout(this.#holdTimer);
    this.#tween?.play();
    this.#currentVideo?.play?.().catch(() => {});
  }

  get #currentVideo() {
    return this.slides[this.#index]?.querySelector('video') ?? null;
  }

  #stop() {
    this.#tween?.stop();
    this.#tween = undefined;
    for (const slide of this.slides) slide.querySelector('video')?.pause();
  }

  #render() {
    this.#stop();

    this.slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('hidden', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });

    // Bars behind the current story are full, ahead of it are empty; only the
    // current one animates.
    this.bars.forEach((bar, i) => {
      bar.style.setProperty('--progress', i < this.#index ? '1' : '0');
    });

    const bar = this.bars[this.#index];
    const video = this.#currentVideo;

    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});
      video.onended = () => this.go(1);
      video.ontimeupdate = () => {
        if (!video.duration) return;
        bar?.style.setProperty('--progress', String(video.currentTime / video.duration));
      };
      return;
    }

    if (prefersReducedMotion()) {
      // No auto-advance without motion: the reader controls the pace.
      bar?.style.setProperty('--progress', '1');
      return;
    }

    this.#tween = animate(0, 1, {
      duration: STILL_DURATION,
      ease: 'linear',
      onUpdate: (value) => bar?.style.setProperty('--progress', String(value)),
      onComplete: () => this.go(1),
    });
  }
}

if (!customElements.get('nuvrya-story-player')) {
  customElements.define('nuvrya-story-player', StoryPlayer);
}

export { StoryPlayer };

/* =============================================================
 * Auto-scrolling story rail
 *
 * The reference runs this rail as a marquee. Its slider config says so
 * (`"marquee": 1, "pauseOnHover": true`, looping above 1025px), and the
 * measured animation on its marquee inner is `transform`, 33077ms, linear,
 * infinite — a compositor-driven transform, not a scrolled container.
 *
 * A first attempt drove `scrollLeft` from requestAnimationFrame. Two things
 * killed it, both worth recording:
 *   1. the rail carries `scroll-snap-type: x mandatory`, so every sub-pixel
 *      step was snapped straight back to the nearest card and the rail sat
 *      frozen on a snap point;
 *   2. rAF does not tick at all in a renderer that is not painting, so the
 *      motion silently stopped wherever the frame loop was throttled.
 *
 * A CSS transform animation has neither problem: snapping does not apply, and
 * the compositor keeps it running without a script frame. The track is
 * duplicated until it overfills the rail by a whole copy, and the wrap shifts
 * by one copy's exact stride; clones are hidden from assistive tech and removed
 * from the tab order so no story is announced or focused twice.
 * ============================================================= */
{
  const RM = matchMedia('(prefers-reduced-motion: reduce)');
  const PX_PER_SECOND = 40;

  const init = () => {
    for (const rail of document.querySelectorAll('.nuvrya-stories__rail')) {
      if (rail.dataset.marqueeReady) continue;
      const originals = [...rail.children];
      if (!originals.length) continue;
      rail.dataset.marqueeReady = 'true';

      // Touch keeps the native swipe rail: there is no hover to pause a
      // marquee with, and hiding the overflow there would take away the only
      // way to reach the later stories. The reference likewise only loops the
      // rail from 1025px up.
      if (RM.matches || !matchMedia('(hover: hover) and (pointer: fine)').matches) continue;

      // Snapping and a transform marquee are mutually exclusive, and the
      // reference scrolls this rail freely (`dragFree: true`) anyway.
      rail.style.scrollSnapType = 'none';
      rail.style.overflowX = 'hidden';

      const track = document.createElement('div');
      track.className = 'nuvrya-stories__marquee';
      track.setAttribute('role', 'list');
      rail.setAttribute('role', 'presentation');
      track.style.display = 'flex';
      track.style.gap = getComputedStyle(rail).gap;
      track.style.willChange = 'transform';

      for (const node of originals) track.appendChild(node);
      rail.appendChild(track);

      // A clone must not carry reveal state. `nuvrya-reveal.js` marks the
      // originals with `can-reveal` and writes the pre-reveal inline style
      // (`opacity: 0; transform: translate(...)`) before its observer fires;
      // cloneNode(true) copies both, and because the clone never existed when
      // the observer swept, nothing ever animates it in. The clones sat at
      // opacity 0 for good, so the rail showed only the four originals and
      // everything past them was an empty band.
      const stripReveal = (el) => {
        for (const node of [el, ...el.querySelectorAll('[can-reveal], [reveal]')]) {
          node.removeAttribute('can-reveal');
          node.removeAttribute('reveal');
          node.removeAttribute('reveal-distance');
          node.removeAttribute('reveal-repeat');
          node.style.opacity = '';
          node.style.transform = '';
        }
      };

      const addClones = () => {
        for (const node of originals) {
          const clone = node.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          stripReveal(clone);
          for (const f of clone.querySelectorAll('a,button,[tabindex]')) f.setAttribute('tabindex', '-1');
          track.appendChild(clone);
        }
      };

      // The originals need the same treatment. The rail carries
      // `nuvrya-reveal--disabled` so the engine should never mark them, but if
      // anything did mark them before this ran, a primed card that the marquee
      // carries out of the clip never intersects again and stays invisible.
      rail.removeAttribute('reveal-on-scroll');
      for (const node of originals) stripReveal(node);

      // One copy's STRIDE, not half the track. A flex track holding c copies of
      // n items carries (c*n - 1) gaps, so half of a two-copy track is one copy
      // minus half a gap - the loop used to land 12.8px short every pass and
      // the seam showed as a jump.
      const measureStride = () => {
        const kids = [...track.children];
        if (kids.length <= originals.length) return track.scrollWidth;
        return kids[originals.length].getBoundingClientRect().left - kids[0].getBoundingClientRect().left;
      };

      // Clone until the track overfills the rail by a whole copy. With one
      // duplicate a 4-card rail is 2585.6 wide inside a 1525.6 viewport: at the
      // end of the pass the tail has moved past the right edge and the rail
      // empties out. It needs railWidth + stride at minimum.
      addClones();
      let stride = measureStride();
      const needed = rail.clientWidth + stride;
      let guard = 8;
      while (track.scrollWidth < needed && guard--) {
        addClones();
        stride = measureStride();
      }

      track.style.setProperty('--nuvrya-marquee-shift', `${stride}px`);

      // Duration from the stride keeps the speed constant whatever the card
      // count or the number of copies it took to fill the rail.
      const seconds = Math.max(12, stride / PX_PER_SECOND);
      track.style.animation = `nuvrya-stories-marquee ${seconds}s linear infinite`;

      // The copy count is measured once at load, so widening the window can
      // leave the track short again and the rail drains on the right. Top it
      // up when the rail grows.
      if ('ResizeObserver' in window) {
        let last = rail.clientWidth;
        new ResizeObserver(() => {
          if (rail.clientWidth <= last) return;
          last = rail.clientWidth;
          let topUp = 8;
          while (track.scrollWidth < rail.clientWidth + stride && topUp--) addClones();
        }).observe(rail);
      }

      rail.addEventListener('pointerenter', () => { track.style.animationPlayState = 'paused'; });
      rail.addEventListener('pointerleave', () => { track.style.animationPlayState = 'running'; });
      rail.addEventListener('focusin', () => { track.style.animationPlayState = 'paused'; });
      rail.addEventListener('focusout', () => { track.style.animationPlayState = 'running'; });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
